import { makeAutoObservable } from "mobx";
import type {
  InstanceActionPhase,
  InstanceActionProgressEvent,
  InstanceActionPullProgress,
  InstanceActionType,
} from "../../infrastructure.types";

/**
 * Follows the steps of ONE container action while the call that started it
 * is in flight — what the confirm / deploy / rollback / move dialogs show in
 * place of a bare spinner.
 *
 * The API emits one `instance-action-progress` event per step (see
 * `packages/api/src/services/infrastructure/instance-actions/action-progress.ts`).
 * Every action by anyone arrives on the staff channel, so this keeps only:
 *   - events carrying ITS token — the correlation id the dialog generated
 *     and sent with the request, which the API echoes on every event of that
 *     request's run. Another operator's action on the same container, even
 *     one already under way when this dialog subscribed, has a different
 *     token (or none) and is ignored;
 *   - for its instance (GUIDs compared case-insensitively), as a belt;
 *   - in `seq` order (a late duplicate is dropped).
 *
 * It claims nothing it has not received: {@link phase} is null until the
 * first event, and the panel says so. A repeated phase (the pull reports its
 * counts every few hundred ms) updates the step in place rather than
 * appending, so {@link steps} is the distinct list in the order reported.
 */

export interface ActionProgressStep {
  phase: InstanceActionPhase;
  /** When this step was FIRST reported (ms since epoch, client clock). */
  at: number;
  pull?: InstanceActionPullProgress;
  waitUpToMs?: number;
  /** Steps the API said, at this point, it will not take. */
  skips?: InstanceActionPhase[];
}

/** GUID equality that ignores case (MSSQL answers UNIQUEIDENTIFIER upper-case). */
const sameId = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

export default class ActionProgressWatch {
  instanceId: string | null = null;
  action: InstanceActionType | null = null;
  /** The correlation token sent with the request; only events echoing it count. */
  token: string | null = null;
  /** When the action was sent (client clock), for the elapsed display. */
  startedAt: number | null = null;
  /** The API's id for the run being followed; null until its first event. */
  runId: string | null = null;
  steps: ActionProgressStep[] = [];
  private lastSeq = 0;

  constructor(private readonly now: () => number = () => Date.now()) {
    makeAutoObservable<ActionProgressWatch, "now">(this, { now: false });
  }

  /** An action is in flight. */
  get active(): boolean {
    return this.instanceId !== null;
  }

  /** The latest step reported, or null when nothing has arrived. */
  get phase(): InstanceActionPhase | null {
    return this.steps.length ? this.steps[this.steps.length - 1].phase : null;
  }

  /** The latest pull counts, while (or after) the image is pulled. */
  get pull(): InstanceActionPullProgress | null {
    for (let i = this.steps.length - 1; i >= 0; i -= 1) {
      if (this.steps[i].pull) return this.steps[i].pull ?? null;
    }
    return null;
  }

  /** The health wait's bound, once that step was reported. */
  get waitUpToMs(): number | null {
    const step = this.steps.find((s) => s.phase === "health");
    return step?.waitUpToMs ?? null;
  }

  /** Every step the API has said this run will not take. */
  get skips(): InstanceActionPhase[] {
    const out: InstanceActionPhase[] = [];
    for (const step of this.steps) for (const s of step.skips ?? []) if (!out.includes(s)) out.push(s);
    return out;
  }

  /** Follow the next action on `instanceId`, sent with `token`. */
  begin(instanceId: string, action: InstanceActionType, token: string): void {
    this.instanceId = instanceId;
    this.action = action;
    this.token = token;
    this.startedAt = this.now();
    this.runId = null;
    this.steps = [];
    this.lastSeq = 0;
  }

  /**
   * Take one event off the channel. Returns true when it belonged to the
   * action being followed and was applied.
   */
  apply(event: InstanceActionProgressEvent): boolean {
    if (!this.active || this.token === null) return false;
    if (event.token !== this.token) return false;
    if (!sameId(event.instanceId, this.instanceId)) return false;
    if (this.runId === null) this.runId = event.runId;
    else if (event.runId !== this.runId) return false;
    if (typeof event.seq === "number") {
      if (event.seq <= this.lastSeq) return false;
      this.lastSeq = event.seq;
    }
    const extras: Pick<ActionProgressStep, "pull" | "waitUpToMs" | "skips"> = {
      ...(event.pull ? { pull: event.pull } : {}),
      ...(typeof event.waitUpToMs === "number" ? { waitUpToMs: event.waitUpToMs } : {}),
      ...(Array.isArray(event.skips) && event.skips.length ? { skips: [...event.skips] } : {}),
    };
    const last = this.steps[this.steps.length - 1];
    if (last && last.phase === event.phase) {
      Object.assign(last, extras);
    } else {
      this.steps.push({ phase: event.phase, at: this.now(), ...extras });
    }
    return true;
  }

  /** The action's call returned (either way); nothing is followed. */
  end(): void {
    this.instanceId = null;
    this.action = null;
    this.token = null;
    this.startedAt = null;
    this.runId = null;
    this.steps = [];
    this.lastSeq = 0;
  }
}
